import { SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import {
  AfterUpsertAnswerAction,
  FullAnswerFragment,
  useGetFactAnswerDetailsQuery,
  useUpdateAnswerMutation,
} from '../../../../graphql/gen/types';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { logAnalytics } from '../../shared/logAnalytics';
import { ConcurrentUpdateSnackbar } from '../ConcurrentUpdateSnackbar';
import { useDialogState } from '../useDialogState';
import { FactAnswerDialogContent, FactAnswerInput } from './FactAnswerDialogContent';

type Props = {
  factAnswerId: string;
};

type State = (FactAnswerInput & { hasConcurrentUpdate: boolean }) | null;

const initializeState = (
  setState: React.Dispatch<React.SetStateAction<State>>,
  factAnswer: FullAnswerFragment,
  options?: { initializeOnlyIfEmpty: boolean }
): void => {
  setState((state) =>
    state == null || !options?.initializeOnlyIfEmpty
      ? {
          body: SlateEditorValue.from(factAnswer.body),
          entities: { expanded_link: factAnswer.links, mention: factAnswer.mentions },
          primaryLinkId: factAnswer.primaryLink?.id,
          hasConcurrentUpdate: false,
        }
      : { ...state, hasConcurrentUpdate: true }
  );
};

export function UpdateFactAnswerDialog({ factAnswerId }: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();

  const [state, setState] = useState<State>(null);
  // TODO: error handling
  const { data, loading } = useGetFactAnswerDetailsQuery({ variables: { factAnswerId } });
  // TODO: error handling
  const [updateFact, { loading: updateLoading }] = useUpdateAnswerMutation();
  const fileUploading = state?.body != null && SlateUtils.hasUploadsInProgress(state.body.value);

  const factAnswer: FullAnswerFragment | null | undefined = data?.loggedIn?.factAnswerById;

  useEffect(() => {
    if (!loading && factAnswer != null) {
      initializeState(setState, factAnswer, { initializeOnlyIfEmpty: true });
    }
  }, [factAnswer, loading]);

  const handleClose = (): void => {
    setDialogState(null);
  };

  const handleSave = (afterAction: AfterUpsertAnswerAction): void => {
    if (state != null) {
      updateFact({
        variables: {
          answer: {
            id: factAnswerId,
            body: state.body.value,
            primaryLinkId: state.primaryLinkId,
            afterAction,
          },
        },
      }).then(({ data }) => {
        if (data?.updateAnswer != null) {
          handleClose();
          logAnalytics({
            name: 'answer updated',
            details: { itemId: factAnswer?.question.id, plexId: factAnswer?.question.plex.id },
          });
        }
      });
    }
  };

  const handleChange = (factAnswerInput: FactAnswerInput): void => {
    if (state != null) {
      setState({ ...state, ...factAnswerInput });
    }
  };

  const question = factAnswer?.question;
  if (!question || !factAnswer || !state) {
    return <CircularProgress size={24} />;
  }

  return (
    <>
      <FactAnswerDialogContent
        mode="update"
        isApprovedAnswer={factAnswerId === factAnswer.question.acceptedAnswerId}
        value={state}
        question={factAnswer.question}
        onChange={handleChange}
        actionButtonOnClick={handleSave}
        actionButtonDisabled={loading || fileUploading || updateLoading}
      />

      <ConcurrentUpdateSnackbar
        open={state?.hasConcurrentUpdate && !updateLoading}
        onRefresh={(): void => {
          if (factAnswer != null) {
            initializeState(setState, factAnswer);
          }
        }}
        onDismiss={(): void => {
          setState((initialState) =>
            initialState != null ? { ...initialState, hasConcurrentUpdate: false } : initialState
          );
        }}
      />
    </>
  );
}
